import { useEffect, useLayoutEffect } from 'react';
import type { HeaderMetaConfigState } from './HeaderMetaConfigContext';
import { useHeaderMetaConfigContext } from './HeaderMetaConfigContext';

type HeaderMetaInputValues = Parameters<HeaderMetaConfigState['setInputValues']>[0];

const emptyHeaderMetaInputValues: HeaderMetaInputValues = { name: '', description: '' };
const noopSetHeaderMetaInputValues: HeaderMetaConfigState['setInputValues'] = () => {};

/**
 * Used in two modes:
 * 1. From HeaderMetaConfiguration (with name/description args): syncs props into context.
 *    Sync runs via useLayoutEffect so the context updates before browser paint.
 *    Consumers reading metaName/metaDescription cannot observe stale values from a
 *    prior resource (for example, when navigating source to sink within the same flow).
 *    On unmount the writer clears the context so the next resource starts clean.
 * 2. From resource creation hooks (no args): reads name/description from context.
 *
 * Gracefully degrades to empty strings when rendered outside a HeaderMetaConfigProvider.
 */
export function useHeaderMetaConfig(initialName?: string, initialDescription?: string) {
  const context = useHeaderMetaConfigContext();
  const isHeaderMetaWriter = initialName !== undefined || initialDescription !== undefined;
  const setHeaderMetaInputValues = context?.setInputValues;
  const safeSetHeaderMetaInputValues = setHeaderMetaInputValues ?? noopSetHeaderMetaInputValues;
  const currentMetaName = context?.name ?? '';
  const currentMetaDescription = context?.description ?? '';

  useLayoutEffect(() => {
    if (!isHeaderMetaWriter || !setHeaderMetaInputValues) return;

    const headerMetaInputValues: HeaderMetaInputValues = {};

    if (initialName !== undefined) {
      headerMetaInputValues.name = initialName;
    }

    if (initialDescription !== undefined) {
      headerMetaInputValues.description = initialDescription;
    }

    setHeaderMetaInputValues(headerMetaInputValues);
  }, [initialName, initialDescription, isHeaderMetaWriter, setHeaderMetaInputValues]);

  useEffect(() => {
    if (!isHeaderMetaWriter || !setHeaderMetaInputValues) return;

    return () => {
      setHeaderMetaInputValues(emptyHeaderMetaInputValues);
    };
  }, [isHeaderMetaWriter, setHeaderMetaInputValues]);

  return {
    name: currentMetaName,
    description: currentMetaDescription,
    setInputValues: safeSetHeaderMetaInputValues,
  };
}
